/**
 * INTERNATIONALIZATION-LOCALE-AND-MULTILINGUAL-EXPERIENCE-FOUNDATION-V0
 * Supported locale canon — exactly three locales.
 */

export const I18N_FOUNDATION_PROGRAM_ID_V0 =
  'INTERNATIONALIZATION-LOCALE-AND-MULTILINGUAL-EXPERIENCE-FOUNDATION-V0' as const;

export type SupportedLocale = 'ko-KR' | 'en' | 'ja-JP';

export const SUPPORTED_LOCALES_V0 = ['ko-KR', 'en', 'ja-JP'] as const;

export const FALLBACK_LOCALE_V0: SupportedLocale = 'en';

export interface LocaleMetadataV0 {
  code: SupportedLocale;
  label: string;
  nativeLabel: string;
  direction: 'ltr';
  htmlLang: string;
  antdLocaleKey: 'ko_KR' | 'en_US' | 'ja_JP';
}

export const LOCALE_METADATA_V0: Record<SupportedLocale, LocaleMetadataV0> = {
  'ko-KR': {
    code: 'ko-KR',
    label: '한국어',
    nativeLabel: '한국어',
    direction: 'ltr',
    htmlLang: 'ko',
    antdLocaleKey: 'ko_KR',
  },
  en: {
    code: 'en',
    label: 'English',
    nativeLabel: 'English',
    direction: 'ltr',
    htmlLang: 'en',
    antdLocaleKey: 'en_US',
  },
  'ja-JP': {
    code: 'ja-JP',
    label: '日本語',
    nativeLabel: '日本語',
    direction: 'ltr',
    htmlLang: 'ja',
    antdLocaleKey: 'ja_JP',
  },
};

/** Native labels for language selector — never translate these. */
export const LANGUAGE_SELECTOR_NATIVE_LABELS_V0: Record<SupportedLocale, string> = {
  'ko-KR': '한국어',
  en: 'English',
  'ja-JP': '日本語',
};

export function isSupportedLocale(value: unknown): value is SupportedLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES_V0 as readonly string[]).includes(value);
}

/** Normalize browser/query variants into SupportedLocale or null. */
export function normalizeLocaleInput(value: string | null | undefined): SupportedLocale | null {
  if (!value) return null;
  const raw = value.trim();
  if (isSupportedLocale(raw)) return raw;
  const lower = raw.toLowerCase().replace('_', '-');
  if (lower === 'ko' || lower === 'ko-kr' || lower.startsWith('ko-')) return 'ko-KR';
  if (lower === 'en' || lower === 'en-us' || lower === 'en-gb' || lower.startsWith('en-'))
    return 'en';
  if (lower === 'ja' || lower === 'ja-jp' || lower.startsWith('ja-')) return 'ja-JP';
  return null;
}

export function resolveSafeLocale(value: unknown): SupportedLocale {
  if (isSupportedLocale(value)) return value;
  if (typeof value === 'string') {
    const normalized = normalizeLocaleInput(value);
    if (normalized) return normalized;
  }
  return FALLBACK_LOCALE_V0;
}
